/**
 * Client-side validators that mirror the backend constraints. Validation is
 * duplicated on purpose: the server is authoritative, but failing fast in
 * the browser gives instant feedback without burning a round-trip.
 */

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
const PHONE_RE = /^\+?\d{6,15}$/;
const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=]).{8,}$/;

export type FieldError = string | null;

export const validators = {
  email(value: string): FieldError {
    const v = value.trim();
    if (!v) return 'auth.errors.emailRequired';
    if (!EMAIL_RE.test(v)) return 'auth.errors.emailInvalid';
    return null;
  },

  phone(value: string): FieldError {
    // Strip spaces / dashes / parens before testing — UI noise, not data.
    const stripped = value.replace(/[\s\-()]/g, '');
    if (!stripped) return 'auth.errors.phoneRequired';
    if (!PHONE_RE.test(stripped)) return 'auth.errors.phoneInvalid';
    return null;
  },

  password(value: string): FieldError {
    if (!value) return 'auth.errors.passwordRequired';
    if (value.length < 8 || value.length > 128) return 'auth.errors.passwordLength';
    if (!PASSWORD_RE.test(value)) return 'auth.errors.passwordComplexity';
    return null;
  },

  verificationCode(value: string): FieldError {
    if (!value) return 'auth.errors.codeRequired';
    if (!/^\d{6}$/.test(value)) return 'auth.errors.codeFormat';
    return null;
  },
};

/** Normalise a phone number before submission: keep "+", strip everything else
 *  that isn't a digit. The backend regex is strict (`^\+?\d{6,15}$`). */
export function normalisePhone(input: string): string {
  const trimmed = input.trim();
  const startsWithPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  return startsWithPlus ? `+${digits}` : digits;
}
